import { getBackendUrl } from '@/utils/apiUrl';
import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

const DATMyClaimIFrame = ({ caseId, onCalculationComplete, onContractIdReceived, vehicleData }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [iframeUrl, setIframeUrl] = useState(null);
  const [contractId, setContractId] = useState(null);
  const iframeRef = useRef(null);

  useEffect(() => {
    // Generate DAT token and build iframe URL
    const initializeDATIFrame = async () => {
      try {
        setLoading(true);
        setError(null);

        // Prepare request body with vehicle data if provided
        const requestBody = vehicleData ? {
          vin: vehicleData.vin || '',
          license_plate: vehicleData.license_plate || '',
          manufacturer: vehicleData.manufacturer || '',
          model: vehicleData.model || '',
          customer_name: vehicleData.customer_name || '',
          damage_location: vehicleData.damage_location || '',
          damage_description: vehicleData.damage_description || ''
        } : { caseId };

        // Get DAT credentials and generate token
        const response = await fetch(`${getBackendUrl()}/api/dat/generate-iframe-url`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
          throw new Error('Fehler beim Generieren der DAT-URL');
        }

        const data = await response.json();
        
        if (data.success && data.iframe_url) {
          setIframeUrl(data.iframe_url);
          setContractId(data.contract_id);
          
          if (data.contract_id) {
            toast.success(`Neuer Glasauftrag erstellt (ID: ${data.contract_id})`);
            // Notify parent component about contract ID
            if (onContractIdReceived) {
              onContractIdReceived(data.contract_id);
            }
          } else {
            toast.success('DAT myClaim wird geladen...');
          }
        } else {
          throw new Error(data.message || 'Konnte DAT-URL nicht generieren');
        }

      } catch (err) {
        console.error('DAT IFrame Fehler:', err);
        setError(err.message);
        toast.error('Fehler beim Laden von DAT myClaim: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    initializeDATIFrame();
  }, [caseId, vehicleData]);

  useEffect(() => {
    // Suppress cross-origin script errors from DAT IFrame
    const handleError = (event) => {
      if (event.message === 'Script error.' && event.filename === '') {
        // This is a cross-origin error from the DAT IFrame - ignore it
        event.preventDefault();
        return true;
      }
    };
    
    window.addEventListener('error', handleError, true);
    
    // Try to trigger "New Contract" in DAT after iframe loads
    const triggerNewContract = () => {
      if (iframeRef.current && vehicleData && vehicleData.vin) {
        try {
          // Send message to DAT iframe to create new contract
          // (This might not work due to cross-origin restrictions, but worth trying)
          iframeRef.current.contentWindow.postMessage({
            action: 'createNewContract',
            contractType: 'glass',
            vin: vehicleData.vin,
            licensePlate: vehicleData.license_plate,
            customerName: vehicleData.customer_name
          }, 'https://www.dat.de');
          
        } catch (error) {
        }
      }
    };
    
    // Trigger after iframe loads (delay to ensure DAT is ready)
    const timer = setTimeout(triggerNewContract, 3000);
    
    // Listen for messages from DAT myClaim IFrame
    const handleMessage = (event) => {
      // Security: Check origin
      if (!event.origin.includes('dat.de')) {
        return;
      }

      const msgData = event.data;
      
      // Check if it's a myClaim event
      if (msgData && msgData.type === 'event') {

        switch (msgData.event) {
          case 'inbox_valuated':
            // New calculation/valuation completed
            toast.success('Kalkulation abgeschlossen!');
            if (onCalculationComplete) {
              onCalculationComplete(msgData.data);
            }
            break;

          case 'refresh_calculation':
            // Calculation updated
            if (onCalculationComplete) {
              onCalculationComplete(msgData.data);
            }
            break;

          case 'inbox_statusChanged':
            // Status changed
            toast.info(`Status: ${msgData.data.status?.name || 'Geändert'}`);
            break;

          case 'inbox_templateSaved':
            // Claim saved
            toast.success('Auftrag gespeichert!');
            break;

          case 'inbox_partnerChange':
            // Partner changed
            break;

          default:
        }
      }
    };

    // Add event listener
    window.addEventListener('message', handleMessage, false);

    // Cleanup
    return () => {
      clearTimeout(timer);
      window.removeEventListener('error', handleError, true);
      window.removeEventListener('message', handleMessage, false);
    };
  }, [onCalculationComplete, vehicleData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-lg text-gray-700">DAT myClaim wird geladen...</p>
          <p className="text-sm text-gray-500 mt-2">Token wird generiert und Verbindung wird hergestellt</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-red-50">
        <div className="text-center p-8 bg-white rounded-lg shadow-lg max-w-md">
          <div className="text-red-600 text-5xl mb-4">⚠️</div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Fehler beim Laden</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Neu laden
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-screen">
      {/* IFrame container */}
      <iframe
        ref={iframeRef}
        src={iframeUrl}
        width="100%"
        height="100%"
        frameBorder="0"
        style={{
          border: 'none',
          minHeight: '800px'
        }}
        title="DAT myClaim"
        allow="clipboard-read; clipboard-write"
        data-testid="dat-myclaim-iframe"
      />
      
      {/* Watermark to show it's embedded */}
      <div className="absolute top-4 right-4 bg-blue-600 text-white px-3 py-1 rounded-lg text-sm shadow-lg opacity-80">
        DAT myClaim Integration
      </div>
    </div>
  );
};

export default DATMyClaimIFrame;
